import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Menu, ChevronDown, Heart, Package, LogOut, UserCircle, X, Home, ShoppingBag, FileText, Info, Truck, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LiveSearchDropdown } from '@/components/search/LiveSearchDropdown';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCategories } from '@/hooks/useProducts';
import { ShopifyCartDrawer } from '@/components/shopify/ShopifyCartDrawer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import type { Session } from '@supabase/supabase-js';

export const Header: React.FC = () => {
  const { data: categories } = useCategories();
  const [session, setSession] = useState<Session | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        supabase.from('profiles').select('display_name').eq('id', s.user.id).single()
          .then(({ data }) => setDisplayName(data?.display_name || s.user.email || null));
      } else {
        setDisplayName(null);
      }
    });
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        supabase.from('profiles').select('display_name').eq('id', s.user.id).single()
          .then(({ data }) => setDisplayName(data?.display_name || s.user.email || null));
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success('Signed out successfully');
    navigate('/');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-card border-b shadow-sm">
      {/* Top bar */}
      <div className="bg-foreground text-background">
        <div className="container-wide py-1.5 flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Truck className="h-3 w-3" />
              Free Shipping on Orders $35+
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/tracking" className="hover:underline">Track Order</Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container-wide py-4">
        <div className="flex items-center gap-4 lg:gap-8">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>

          <Link to="/" className="flex-shrink-0">
            <span className="text-2xl font-bold tracking-tight">
              <span className="text-primary">Giz</span><span className="text-foreground">Too</span>
            </span>
          </Link>

          <LiveSearchDropdown className="flex-1 hidden sm:block max-w-xl" />

          <div className="flex items-center gap-1 sm:gap-2 ml-auto">
            {session ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Account">
                    <UserCircle className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm font-medium truncate">{displayName}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="w-full cursor-pointer">
                      <User className="h-4 w-4 mr-2" /> My Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/tracking" className="w-full cursor-pointer">
                      <Package className="h-4 w-4 mr-2" /> My Orders
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/wishlist" className="w-full cursor-pointer">
                      <Heart className="h-4 w-4 mr-2" /> Wishlist
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                    <LogOut className="h-4 w-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button variant="ghost" size="icon" aria-label="Sign in">
                  <User className="h-5 w-5" />
                </Button>
              </Link>
            )}
            <ShopifyCartDrawer />
          </div>
        </div>

        <LiveSearchDropdown className="mt-3 sm:hidden" />
      </div>

      {/* Category navigation — desktop */}
      <nav className="hidden lg:block bg-muted border-t">
        <div className="container-wide">
          <ul className="flex items-center gap-1">
            {(categories || []).map((cat) => (
              <li key={cat.id}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-1 font-medium">
                      {cat.name}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to={`/catalog/${cat.slug}`} className="font-medium">
                        All in {cat.name}
                      </Link>
                    </DropdownMenuItem>
                    {cat.subcategories.map((sub) => (
                      <DropdownMenuItem key={sub.id} asChild>
                        <Link to={`/catalog/${cat.slug}/${sub.slug}`}>
                          {sub.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
            <li>
              <Link to="/blog">
                <Button variant="ghost" className="font-medium">📝 Blog</Button>
              </Link>
            </li>
            <li className="ml-auto">
              <Link to="/deals">
                <Button variant="ghost" className="font-medium font-bold">🔥 Deals</Button>
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile slide-in menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={closeMobileMenu} />

          <nav className="absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-card shadow-xl animate-slide-in-left flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="text-xl font-bold">
                <span className="text-primary">Giz</span><span className="text-foreground">Too</span>
              </span>
              <Button variant="ghost" size="icon" onClick={closeMobileMenu} aria-label="Close menu">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              <Link to="/" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors font-medium">
                <Home className="h-5 w-5 text-muted-foreground" /> Home
              </Link>

              <Separator className="my-2" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-2">Categories</p>

              {(categories || []).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/catalog/${cat.slug}`}
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                  {cat.name}
                </Link>
              ))}

              <Separator className="my-2" />

              <Link to="/blog" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors">
                <FileText className="h-4 w-4 text-muted-foreground" /> Blog
              </Link>
              <Link to="/deals" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors font-semibold">
                🔥 Deals
              </Link>
              <Link to="/wishlist" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors">
                <Heart className="h-4 w-4 text-muted-foreground" /> Wishlist
              </Link>
              <Link to="/tracking" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors">
                <Package className="h-4 w-4 text-muted-foreground" /> Track Order
              </Link>

              <Separator className="my-2" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-2">Help</p>

              <Link to="/about" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors">
                <Info className="h-4 w-4 text-muted-foreground" /> About Us
              </Link>
              <Link to="/shipping" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors">
                <Truck className="h-4 w-4 text-muted-foreground" /> Shipping & Payment
              </Link>
              <Link to="/contact" onClick={closeMobileMenu} className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted transition-colors">
                <Mail className="h-4 w-4 text-muted-foreground" /> Contact
              </Link>
            </div>

            <div className="p-4 border-t">
              {session ? (
                <div className="space-y-2">
                  <Link to="/account" onClick={closeMobileMenu} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted transition-colors">
                    <UserCircle className="h-5 w-5 text-muted-foreground" />
                    <span className="truncate">{displayName || 'My Account'}</span>
                  </Link>
                  <Button variant="outline" className="w-full gap-2" onClick={() => { handleSignOut(); closeMobileMenu(); }}>
                    <LogOut className="h-4 w-4" /> Sign Out
                  </Button>
                </div>
              ) : (
                <Link to="/auth" onClick={closeMobileMenu}>
                  <Button className="w-full gap-2">
                    <User className="h-4 w-4" /> Sign In / Register
                  </Button>
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};